import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';

const REFRESH_MS = 30_000;

function viewModel(payload = {}) {
  const health = payload.health || {};
  if (!payload.configured || health.state === 'not_configured') {
    return {
      key: 'not_configured',
      tone: 'border-amber-200 bg-amber-50 text-amber-900',
      title: 'LinkedIn-verbinding instellen',
      message: 'Kies eerst je land zodat Leadgenflow een vaste managed verbinding kan configureren. Automatisering blijft tot die tijd gepauzeerd.',
      action: 'Naar instellingen',
    };
  }
  if (health.supportRequired || health.state === 'auth_failed') {
    return {
      key: 'support_required',
      tone: 'border-red-200 bg-red-50 text-red-900',
      title: 'LinkedIn-automatisering heeft aandacht nodig',
      message: 'Leadgenflow heeft de automatisering veilig gepauzeerd en support recovery gestart. Actieve campagnes en wachtrijtaken blijven behouden.',
      action: 'Bekijk accountinstellingen',
    };
  }
  if (health.automationPaused || health.state === 'unavailable') {
    return {
      key: 'paused',
      tone: 'border-amber-200 bg-amber-50 text-amber-900',
      title: 'LinkedIn-automatisering tijdelijk gepauzeerd',
      message: 'Er is een tijdelijk verbindingsprobleem. Leadgenflow probeert automatisch te herstellen; campagnes hoeven niet handmatig gestopt te worden.',
      action: 'Bekijk status',
    };
  }
  if (health.state === 'degraded') {
    return {
      key: 'degraded',
      tone: 'border-sky-200 bg-sky-50 text-sky-900',
      title: 'LinkedIn-verbinding wordt bewaakt',
      message: 'De verbinding is tijdelijk minder stabiel. Leadgenflow blijft controleren en pauzeert automatisch als dat nodig wordt.',
      action: 'Bekijk status',
    };
  }
  if (health.healthy) return null;
  return {
    key: 'checking',
    tone: 'border-slate-200 bg-slate-50 text-slate-800',
    title: 'LinkedIn-verbinding wordt gecontroleerd',
    message: 'Leadgenflow controleert de managed verbinding voordat automatisering wordt uitgevoerd.',
    action: 'Bekijk status',
  };
}

export default function AccountHealthBanner() {
  const [health, setHealth] = useState(null);
  const [recovered, setRecovered] = useState(false);
  const previousProblem = useRef(false);

  useEffect(() => {
    let active = true;
    let recoveryTimer;

    const load = async () => {
      try {
        const response = await api.get('/linkedin/network-health');
        if (!active) return;
        const payload = response.data || null;
        const model = viewModel(payload || {});
        const hasProblem = Boolean(model && ['not_configured', 'support_required', 'paused', 'degraded'].includes(model.key));
        if (previousProblem.current && !hasProblem && payload?.health?.healthy) {
          setRecovered(true);
          window.clearTimeout(recoveryTimer);
          recoveryTimer = window.setTimeout(() => active && setRecovered(false), 12000);
        }
        previousProblem.current = hasProblem;
        setHealth(payload);
      } catch {
        // A health banner must never block the application when the status endpoint is unavailable.
      }
    };

    load();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.clearTimeout(recoveryTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (recovered) {
    return (
      <div className="mx-4 mt-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900 lg:mx-6">
        <strong>LinkedIn-verbinding hersteld.</strong>{' '}
        Automatisering kan weer normaal verder; bestaande campagnes en wachtrijtaken blijven intact.
      </div>
    );
  }

  const model = viewModel(health || {});
  if (!health || !model) return null;

  return (
    <div className={`mx-4 mt-3 flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between lg:mx-6 ${model.tone}`}>
      <div>
        <strong className="block">{model.title}</strong>
        <span>{model.message}</span>
      </div>
      <Link to="/settings?tab=account" className="shrink-0 font-semibold underline underline-offset-2">
        {model.action}
      </Link>
    </div>
  );
}

export { viewModel };
